import { SearchField } from '@ui/SearchField'
import { observer } from 'mobx-react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Training, TrainingsProxy, trainingQueryKeys } from '@entities/training'
import { SearchParams } from '@shared/types/searchParams'
import { getCurrentBackendLocale } from '@shared/i18n'
import { getMediaUrl } from '@shared/helpers/getMediaUrl'
import { ROUTES } from '@shared/constants/routes'
import { useHistory } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Clock3, Dumbbell } from 'lucide-react'
import { MuscleService } from '@entities/muscle'

type MyTrainingsProps = {
	onOpenExercises?: () => void
}

type TrainingGroupFilter = 'all' | 'CHEST' | 'ARMS' | 'LEGS_GLUTES' | 'SHOULDERS' | 'BACK'
type TrainingSort = 'newest' | 'oldest'

const TRAINING_GROUP_FILTERS: {
	label: string
	value: TrainingGroupFilter
}[] = [
	{ label: 'Все', value: 'all' },
	{ label: 'Грудь', value: 'CHEST' },
	{ label: 'Руки', value: 'ARMS' },
	{ label: 'Ноги', value: 'LEGS_GLUTES' },
	{ label: 'Плечи', value: 'SHOULDERS' },
	{ label: 'Спина', value: 'BACK' },
]

const TRAINING_SORT_OPTIONS: {
	label: string
	value: TrainingSort
}[] = [
	{ label: 'Сначала новые', value: 'newest' },
	{ label: 'Сначала старые', value: 'oldest' },
]

const VISIBLE_PREVIEW_COUNT = 3

function useDebounce(value: string, delay: number): string {
	const [debouncedValue, setDebouncedValue] = useState<string>(value)

	useEffect(() => {
		const handler = window.setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			window.clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

function getTrainingGroupCodes(training: Training): string[] {
	const muscleCodes = new Set<string>(training.muscle_group_codes)

	training.exercises?.forEach((exercise) => {
		exercise.exercise_detail.muscle_group_codes?.forEach((code) => {
			muscleCodes.add(code)
		})
	})

	return MuscleService.getGroupCodesFromMuscleCodes(Array.from(muscleCodes))
}

function getTrainingDate(training: Training): number | null {
	const timestamp = new Date(training.created_at).getTime()
	return Number.isFinite(timestamp) ? timestamp : null
}

function sortTrainings(trainings: Training[], sort: TrainingSort): Training[] {
	return [...trainings].sort((a, b) => {
		const firstDate = getTrainingDate(a)
		const secondDate = getTrainingDate(b)

		if (firstDate === null || secondDate === null) {
			return 0
		}

		return sort === 'newest' ? secondDate - firstDate : firstDate - secondDate
	})
}

function getExerciseCountLabel(count: number): string {
	const lastTwoDigits = count % 100
	const lastDigit = count % 10

	if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
		return `${count} упражнений`
	}

	if (lastDigit === 1) {
		return `${count} упражнение`
	}

	if (lastDigit >= 2 && lastDigit <= 4) {
		return `${count} упражнения`
	}

	return `${count} упражнений`
}

function getTrainingDifficultyLabel(training: Training): string {
	const difficulties = training.exercises
		?.map((exercise) => {
			const exerciseDetail = exercise.exercise_detail as {
				id_difficulty?: number
				difficulty_name?: string
			}

			return exerciseDetail.id_difficulty
		})
		.filter((difficulty): difficulty is number => typeof difficulty === 'number')

	if (!difficulties?.length) {
		return 'Средний'
	}

	const averageDifficulty =
		difficulties.reduce((sum, difficulty) => sum + difficulty, 0) / difficulties.length

	if (averageDifficulty < 1.6) {
		return 'Базовый'
	}

	if (averageDifficulty < 2.6) {
		return 'Средний'
	}

	return 'Продвинутый'
}

function getEstimatedDurationMinutes(training: Training): number {
	const totalSeconds =
		training.exercises?.reduce((sum, exercise) => {
			const sets = exercise.sets ?? 1
			const duration = exercise.duration ?? 45
			const restTime = exercise.rest_time ?? 60
			const workTime = exercise.reps ? exercise.reps * 4 : duration

			return sum + sets * workTime + Math.max(0, sets - 1) * restTime
		}, 0) ?? 0

	return Math.max(1, Math.round(totalSeconds / 60))
}

function getTrainingTimeLabel(training: Training): string {
	const completedAt = training.history?.completed_at ?? training.history?.started_at

	if (completedAt) {
		const date = new Date(completedAt)

		if (Number.isFinite(date.getTime())) {
			return date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
		}
	}

	return `${getEstimatedDurationMinutes(training)} мин`
}

const MyTrainingSkeleton = () => (
	<div className="training-history-skeleton-list" aria-hidden="true">
		{Array.from({ length: 4 }).map((_, index) => (
			<div className="training-history-skeleton-card" key={index}>
				<div className="training-history-skeleton-cover" />
				<div className="training-history-skeleton-body">
					<div className="training-history-skeleton-line is-title" />
					<div className="training-history-skeleton-line is-short" />
					<div className="training-history-skeleton-previews">
						<div />
						<div />
						<div />
					</div>
				</div>
			</div>
		))}
	</div>
)

const TrainingHistoryState = ({
	title,
	description,
	actionLabel,
	onAction,
}: {
	title: string
	description: string
	actionLabel?: string
	onAction?: () => void
}) => (
	<div className="training-history-state">
		<div className="training-history-state-title">{title}</div>
		<div className="training-history-state-description">{description}</div>
		{actionLabel && onAction && (
			<button
				type="button"
				className="training-history-state-action"
				onClick={onAction}
			>
				{actionLabel}
			</button>
		)}
	</div>
)

const TrainingHistoryCard = ({
	training,
	onOpen,
}: {
	training: Training
	onOpen: (training: Training) => void
}) => {
	const exerciseCount = training.exercises?.length ?? 0
	const coverUrl =
		getMediaUrl(training.media?.cover) ||
		getMediaUrl(training.exercises?.[0]?.exercise_detail.media?.icon)
	const previews = training.exercises?.slice(0, VISIBLE_PREVIEW_COUNT) ?? []
	const hiddenCount = Math.max(0, exerciseCount - previews.length)
	const difficultyLabel = getTrainingDifficultyLabel(training)
	const timeLabel = getTrainingTimeLabel(training)

	return (
		<button
			type="button"
			className="training-history-card"
			onClick={() => onOpen(training)}
			aria-label={`Открыть тренировку ${training.name}`}
		>
			<div className="training-history-cover">
				{coverUrl ? (
					<img src={coverUrl} alt="" />
				) : (
					<div className="training-history-cover-placeholder" />
				)}
			</div>

			<div className="training-history-card-body">
				<div className="training-history-card-head">
					<div className="training-history-title">{training.name}</div>
					<div className="training-history-count">
						{getExerciseCountLabel(exerciseCount)}
					</div>
				</div>

				<div className="training-history-previews" aria-hidden="true">
					{previews.map((exercise) => {
						const previewUrl = getMediaUrl(exercise.exercise_detail.media?.icon)

						return (
							<div
								className="training-history-preview"
								key={`${training.id_workout}-${exercise.exercise_detail.id_exercise}-${exercise.order}`}
							>
								{previewUrl ? <img src={previewUrl} alt="" /> : null}
							</div>
						)
					})}
					{hiddenCount > 0 && (
						<div className="training-history-preview training-history-more">
							+{hiddenCount}
						</div>
					)}
				</div>

				<div className="training-history-meta-row">
					<span className="training-history-meta-pill">
						<Dumbbell size={14} strokeWidth={2.4} />
						{difficultyLabel}
					</span>
					<span className="training-history-meta-pill">
						<Clock3 size={14} strokeWidth={2.4} />
						{timeLabel}
					</span>
					<span className="training-history-open-icon" aria-hidden="true">
						<ChevronRight size={20} strokeWidth={2.4} />
					</span>
				</div>
			</div>
		</button>
	)
}

export const MyTrainings = observer(({ onOpenExercises }: MyTrainingsProps) => {
	const [search, setSearch] = useState<string>('')
	const [activeGroup, setActiveGroup] = useState<TrainingGroupFilter>('all')
	const [sort, setSort] = useState<TrainingSort>('newest')
	const [isSortOpen, setIsSortOpen] = useState<boolean>(false)
	const debouncedSearch = useDebounce(search, 400)
	const observerRef = useRef<HTMLDivElement>(null)
	const history = useHistory()
	const limit = 20
	const backendLocale = getCurrentBackendLocale()

	const {
		data,
		isLoading,
		isError,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
		refetch,
	} = useInfiniteQuery<
		Training[],
		Error,
		Training[],
		ReturnType<typeof trainingQueryKeys.list>
	>({
		queryKey: trainingQueryKeys.list({
			scope: 'constructor',
			search: debouncedSearch,
			lang: backendLocale,
		}),
		queryFn: async ({ pageParam = 0 }): Promise<Training[]> => {
			const params: SearchParams = {
				limit,
				start: pageParam,
				page: 0,
				sort: [],
				independent_fields: {},
				filter: [],
			}

			if (debouncedSearch.trim()) {
				return TrainingsProxy.ftsSearch(params, debouncedSearch, backendLocale)
			}

			return TrainingsProxy.getTrainings(params)
		},
		initialPageParam: 0,
		getNextPageParam: (
			lastPage: Training[],
			_allPages: Training[][],
			lastPageParam: number,
		): number | undefined => {
			return lastPage.length < limit ? undefined : lastPageParam + limit
		},
	})

	const trainings: Training[] = useMemo(
		() => data?.pages.flatMap((page) => page) ?? [],
		[data],
	)

	const visibleTrainings = useMemo(() => {
		const filteredTrainings =
			activeGroup === 'all'
				? trainings
				: trainings.filter((training) =>
						getTrainingGroupCodes(training).includes(activeGroup),
					)

		return sortTrainings(filteredTrainings, sort)
	}, [activeGroup, sort, trainings])

	const isFiltered = Boolean(debouncedSearch.trim()) || activeGroup !== 'all'
	const sortLabel =
		TRAINING_SORT_OPTIONS.find((option) => option.value === sort)?.label ??
		'Сначала новые'

	useEffect(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
					fetchNextPage()
				}
			},
			{ threshold: 1.0 },
		)

		if (observerRef.current) {
			observer.observe(observerRef.current)
		}

		return () => observer.disconnect()
	}, [hasNextPage, isFetchingNextPage, fetchNextPage])

	const openTraining = (training: Training) => {
		history.push({
			pathname: ROUTES.training.details.replace(
				':trainingID',
				String(training.id_workout),
			),
			state: {
				trainingDetailsReturnTo: `${ROUTES.overall.trainingConstructor}?tab=my_trainings`,
			},
		})
	}

	const resetFilters = () => {
		setSearch('')
		setActiveGroup('all')
	}

	return (
		<div className="training-my-trainings">
			<section className="training-control-zone training-my-control-zone">
				<SearchField
					data={search}
					setData={setSearch}
					placeholder="Найти тренировку"
					iconPosition="left"
					styles={{ borderRadius: 18 }}
				/>

				<div className="training-my-chips" aria-label="Фильтр тренировок">
					{TRAINING_GROUP_FILTERS.map((filter) => (
						<button
							type="button"
							key={filter.value}
							className={`training-filter-chip training-my-chip ${
								activeGroup === filter.value ? 'is-active' : ''
							}`}
							aria-pressed={activeGroup === filter.value}
							onClick={() => setActiveGroup(filter.value)}
						>
							{filter.label}
						</button>
					))}
				</div>
			</section>

			<div className="training-my-list-scroll">
				<div className="training-history-toolbar">
					<h2>История тренировок</h2>
					<div className="training-history-sort">
						<button
							type="button"
							className="training-history-sort-trigger"
							aria-expanded={isSortOpen}
							onClick={() => setIsSortOpen((current) => !current)}
						>
							{sortLabel}
							<ChevronDown aria-hidden="true" />
						</button>
						{isSortOpen && (
							<div className="training-history-sort-menu">
								{TRAINING_SORT_OPTIONS.map((option) => (
									<button
										type="button"
										key={option.value}
										className={sort === option.value ? 'is-active' : ''}
										onClick={() => {
											setSort(option.value)
											setIsSortOpen(false)
										}}
									>
										{option.label}
									</button>
								))}
							</div>
						)}
					</div>
				</div>

				{isLoading && <MyTrainingSkeleton />}
				{!isLoading && isError && (
					<TrainingHistoryState
						title="Не удалось загрузить тренировки"
						description="Проверьте соединение и попробуйте снова."
						actionLabel="Повторить"
						onAction={() => refetch()}
					/>
				)}
				{!isLoading && !isError && trainings.length === 0 && !isFiltered && (
					<TrainingHistoryState
						title="У вас пока нет тренировок"
						description="Создайте тренировку из упражнений, чтобы она появилась здесь."
						actionLabel="Перейти к упражнениям"
						onAction={onOpenExercises}
					/>
				)}
				{!isLoading && !isError && visibleTrainings.length === 0 && isFiltered && (
					<TrainingHistoryState
						title="Тренировки не найдены"
						description="Попробуйте изменить запрос или сбросить фильтры."
						actionLabel="Сбросить фильтры"
						onAction={resetFilters}
					/>
				)}
				{!isLoading && !isError && visibleTrainings.length > 0 && (
					<div className="training-history-list">
						{visibleTrainings.map((training) => (
							<TrainingHistoryCard
								key={`workout_id_${training.id_workout}`}
								training={training}
								onOpen={openTraining}
							/>
						))}
						{isFetchingNextPage && <MyTrainingSkeleton />}
						<div ref={observerRef} className="training-history-sentinel" />
					</div>
				)}
			</div>
		</div>
	)
})
